import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  MenuItem,
  TextField,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ParentLayout from './Layout';
import { listBillingDocuments } from '../api/billing';
import { listEnrollments } from '../api/enrollments';
import analyticsApi from '../api/analytics';

const Billing = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportLink, setReportLink] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const enr = await listEnrollments({ status: 'active' });
        setEnrollments(enr || []);
        const first = enr?.[0]?.studentId?._id || '';
        setSelectedLearner(first);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load learners');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    return () => {
      if (reportLink) {
        URL.revokeObjectURL(reportLink);
      }
    };
  }, [reportLink]);

  useEffect(() => {
    const loadDocs = async () => {
      if (!selectedLearner) return;
      try {
        setLoading(true);
        const [generalDocs, studentDocs] = await Promise.all([
          listBillingDocuments(),
          listBillingDocuments({ studentId: selectedLearner }),
        ]);
        const merged = [
          ...(Array.isArray(generalDocs) ? generalDocs : []),
          ...(Array.isArray(studentDocs) ? studentDocs : []),
        ];
        const seen = new Set();
        const unique = merged.filter((doc) => {
          if (!doc?._id) return false;
          if (seen.has(doc._id)) return false;
          seen.add(doc._id);
          return true;
        });
        setDocs(unique);

        const blob = await analyticsApi.getTermReport({ studentId: selectedLearner });
        if (blob) {
          const url = URL.createObjectURL(blob);
          setReportLink(url);
        } else {
          setReportLink(null);
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load documents');
      } finally {
        setLoading(false);
      }
    };
    loadDocs();
  }, [selectedLearner]);

  const sections = useMemo(() => {
    const byType = (predicate) => docs.filter(predicate);
    return [
      {
        key: 'registration',
        title: 'Registration fee receipt',
        description: 'Proof of registration payments.',
        items: byType((d) => ['receipt', 'registration', 'registration-receipt'].includes(d.type)),
      },
      {
        key: 'info-pack',
        title: 'Info Pack',
        description: 'Orientation info and onboarding resources.',
        items: byType((d) => ['info-pack', 'info', 'pack'].includes(d.type)),
      },
      {
        key: 'policies',
        title: 'Policies',
        description: 'School policies and guidelines.',
        items: byType((d) => ['policy', 'policies'].includes(d.type)),
      },
      {
        key: 'orientation',
        title: 'Orientation replay',
        description: 'Recorded orientation materials if provided.',
        items: byType((d) => ['orientation', 'orientation-replay', 'replay'].includes(d.type)),
      },
      {
        key: 'reports',
        title: 'Term Report',
        description: 'Term report and progress documents.',
        items: byType((d) => ['report', 'term-report'].includes(d.type)),
        includeReportLink: true,
      },
    ];
  }, [docs]);

  return (
    <ParentLayout>
      <Box>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={800} color="primary.main">
              Billing & Documents
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Download receipts, policies, and info packs shared by admin.
            </Typography>
          </Box>

          {loading && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            select
            label="Select learner"
            value={selectedLearner}
            onChange={(e) => setSelectedLearner(e.target.value)}
            sx={{ maxWidth: 320 }}
          >
            {enrollments.map((enr) => (
              <MenuItem key={enr.studentId?._id} value={enr.studentId?._id}>
                {`${enr.studentId?.name || ''} ${enr.studentId?.surname || ''} (${enr.grade ? `G${enr.grade}` : ''})`}
              </MenuItem>
            ))}
          </TextField>

          <Card variant="outlined">
            <CardHeader title="Billing & Documents" avatar={<ReceiptLongIcon color="primary" />} />
            <CardContent>
              <Stack spacing={2}>
                {docs.length === 0 && !reportLink && (
                  <Typography variant="body2" color="text.secondary">
                    No billing documents available yet.
                  </Typography>
                )}
                {sections.map((section) => (
                  <Card key={section.key} variant="outlined">
                    <CardHeader title={section.title} subheader={section.description} />
                    <CardContent>
                      <Stack spacing={1}>
                        {section.items.length === 0 && !section.includeReportLink && (
                          <Typography variant="body2" color="text.secondary">
                            No documents available.
                          </Typography>
                        )}
                        {section.includeReportLink && (
                          <Stack spacing={1}>
                            {reportLink ? (
                              <Button
                                size="small"
                                variant="outlined"
                                href={reportLink}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Download term report
                              </Button>
 ) : (
                              <Chip size="small" label="Report not ready" />
                            )}
                            {section.items.length > 0 && <Divider />}
                          </Stack>
                        )}
                        {section.items.map((doc) => (
                          <Card key={doc._id} variant="outlined">
                            <CardContent>
                              <Stack spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography fontWeight={700}>{doc.title}</Typography>
                                  <Chip size="small" label={doc.type} />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                  {doc.originalName}
                                </Typography>
                                <Button size="small" variant="outlined" href={doc.url} target="_blank" rel="noreferrer">
                                  Download
                                </Button>
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </ParentLayout>
  );
};

export default Billing;
